import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Sidebar from "@/components/layout/Sidebar";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Copy, HandCoins, Link2, RefreshCw, Users } from "lucide-react";
import DashboardDateTimeWidget from "@/components/layout/DashboardDateTimeWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SalesRepDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sales/referral-summary`);
      setSummary(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to load sales dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  const fullReferralLink = useMemo(() => {
    if (!summary?.referral_link) return "";
    return `${window.location.origin}${summary.referral_link}`;
  }, [summary]);

  const handleCopyLink = async () => {
    if (!fullReferralLink) return;
    try {
      await navigator.clipboard.writeText(fullReferralLink);
      toast.success("Referral link copied");
    } catch (_) {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar role={user?.role} />
      <main className="lg:ml-64 p-6 lg:p-10" data-testid="sales-rep-dashboard">
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-heading font-bold tracking-tight">Sales Referral Command</h1>
              <p className="text-muted-foreground mt-1">Track referred users, load commissions, and wallet credits in real time.</p>
            </div>
            <div className="flex items-center gap-2">
              <DashboardDateTimeWidget />
              <Button variant="outline" onClick={fetchSummary} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-primary" />Unique Referral Link</CardTitle>
              <CardDescription>Share this link with new users to earn lifetime 5% commission credits on qualifying load activity.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                <Input value={fullReferralLink} readOnly />
                <Button onClick={handleCopyLink}><Copy className="w-4 h-4 mr-2" />Copy</Button>
              </div>
              <p className="text-sm text-muted-foreground">Referral code: <span className="font-mono text-foreground">{summary?.referral_code || "-"}</span></p>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground">Referred Users</p>
                <p className="text-3xl font-heading font-bold mt-2">{summary?.totals?.referred_users || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground">Commission Credits</p>
                <p className="text-3xl font-heading font-bold mt-2">${Number(summary?.totals?.total_commission_credits || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground">Wallet Balance</p>
                <p className="text-3xl font-heading font-bold mt-2">${Number(summary?.wallet?.balance_credits || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-primary" />Referred Accounts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(summary?.role_breakdown || {}).map(([role, count]) => (
                    <Badge key={role} variant="outline">{role}: {count}</Badge>
                  ))}
                </div>
                <div className="max-h-96 overflow-y-auto pr-1">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(summary?.referrals || []).length === 0 ? (
                        <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground">No referrals yet</TableCell></TableRow>
                      ) : (
                        summary.referrals.slice(0, 80).map((ref) => (
                          <TableRow key={ref.id}>
                            <TableCell>{ref.name}</TableCell>
                            <TableCell className="capitalize">{String(ref.role || "").replaceAll("_", " ")}</TableCell>
                            <TableCell>{ref.email}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><HandCoins className="w-5 h-5 text-primary" />Commission Events</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-96 overflow-y-auto pr-1">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Amount</TableHead>
                        <TableHead>Shipment</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(summary?.commissions || []).length === 0 ? (
                        <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">No commissions yet</TableCell></TableRow>
                      ) : (
                        summary.commissions.map((tx) => (
                          <TableRow key={tx.id}>
                            <TableCell className="font-semibold text-emerald-400">+${Number(tx.amount || 0).toFixed(2)}</TableCell>
                            <TableCell>{tx.meta?.tracking_number || "-"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{String(tx.source || "").replaceAll("_", " ")}</TableCell>
                            <TableCell>{tx.created_at ? new Date(tx.created_at).toLocaleString() : "-"}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SalesRepDashboard;
